// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState } from 'react';
import { Check, ChevronLeft } from 'lucide-react';
import { ACCREDITATION_DELIVERY, ACCREDITATION_TYPES, ACCREDITATION_ZONES } from '../../constants/people';
import { MOCK } from '../../data/mock';
import { toast } from '../../lib/buses';
import { Field, Input, Select, Textarea } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { EventSelect } from '../../components/filters/EventSelect';

const ACCREDITATION_STEP_SUBS = {
  kind:   'Choose the accreditation category. Default zones load automatically — you can adjust them next.',
  person: 'Who is being accredited, for which event, and how long it stays valid.',
  access: 'Pick the zones this person can enter and how the credential is delivered.',
};

const ACCREDITATION_STEPS = [
  { id: 'kind',   label: '1. Type' },
  { id: 'person', label: '2. Person & event' },
  { id: 'access', label: '3. Access & delivery' },
];

export const IssueAccreditationModal = ({ onClose, onIssue }) => {
  const [step, setStep] = useState('kind'); // 'kind' | 'person' | 'access'
  const [typeId, setTypeId] = useState(null);
  const [name, setName] = useState('');
  const [org, setOrg] = useState('');
  const [contact, setContact] = useState('');
  const [partySize, setPartySize] = useState(1);
  const [eventId, setEventId] = useState('');
  const [validThrough, setValidThrough] = useState('');
  const [delivery, setDelivery] = useState('badge');
  const [zones, setZones] = useState([]);
  const [notes, setNotes] = useState('');

  const type = ACCREDITATION_TYPES.find(t => t.id === typeId);
  const eligibleEvents = MOCK.events.filter(e => e.status === 'Live' || e.status === 'Upcoming' || e.status === 'Draft');
  const selectedEvent = eligibleEvents.find(e => e.id === eventId);

  const pickType = (t) => {
    setTypeId(t.id);
    setZones([...t.defaultZones]);
    setStep('person');
  };
  const toggleZone = (z) => setZones(zs => zs.includes(z) ? zs.filter(x => x !== z) : [...zs, z]);

  const canContinuePerson = name.trim() && eventId && validThrough;
  const canIssue = canContinuePerson && zones.length > 0;

  const issue = () => {
    const newId = 'PRS-' + String(Math.floor(Math.random() * 900 + 100)).padStart(4, '0');
    const displayName = partySize > 1 ? `${name.trim()} (${partySize} pax)` : name.trim();
    const newRow = {
      id: newId,
      name: displayName,
      org: org.trim() || '—',
      role: type.role,
      cred: type.cred,
      zones: zones.includes('All zones') ? 'All zones' : zones.join(', '),
      event: eventId,
      valid: validThrough ? new Date(validThrough).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
      status: 'Issued',
      typeId: type.id, delivery, notes: notes.trim(), partySize,
    };
    onIssue(newRow);
    toast('Accreditation issued', 'success', `${displayName} · ${type.cred} · ${zones.length === 1 ? zones[0] : zones.length + ' zones'}`);
  };

  const stepTitles = {
    kind:   'Issue accreditation — pick a type',
    person: `Issue ${type?.label} — person & event`,
    access: `Issue ${type?.label} — zones & credential`,
  };
  const stepSubs = ACCREDITATION_STEP_SUBS;

  const STEPS = ACCREDITATION_STEPS;

  return (
    <Modal open={true} onClose={onClose} width="max-w-[820px]" title={stepTitles[step]} sub={stepSubs[step]}>
      <div className="flex items-center gap-0 mb-4 border-b border-[#EEEDEA] -mx-5 px-5 pb-0">
        {STEPS.map(s => (
          <div key={s.id} className={`px-3 py-2 text-[11.5px] font-medium border-b-2 ${step === s.id ? 'border-[#1F2937] text-[#1F2937]' : 'border-transparent text-[#9CA3AF]'}`}>{s.label}</div>
        ))}
      </div>

      {step === 'kind' && (
        <div className="grid grid-cols-3 gap-2.5">
          {ACCREDITATION_TYPES.map(t => (
            <button key={t.id} onClick={() => pickType(t)} className="text-left bg-white border border-[#E6E4E1] rounded-md p-3 hover:border-[#1F2937] hover:bg-[#FBFAF7] transition-colors">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded text-white text-[10px] font-bold tracking-[0.04em] flex items-center justify-center" style={{ backgroundColor: t.tone }}>{t.cred.split('-')[0]}</div>
                <div className="text-[12.5px] font-semibold text-[#1F2937]">{t.label}</div>
              </div>
              <div className="text-[10.5px] text-[#6B7280] leading-snug">{t.desc}</div>
              <div className="text-[10px] text-[#9CA3AF] mt-1.5 font-mono">{t.cred}</div>
            </button>
          ))}
        </div>
      )}

      {step === 'person' && type && (
        <div className="space-y-3">
          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-3 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded text-white text-[11px] font-bold flex items-center justify-center" style={{ backgroundColor: type.tone }}>{type.cred.split('-')[0]}</div>
            <div className="flex-1">
              <div className="text-[12.5px] font-semibold text-[#1F2937]">{type.label}</div>
              <div className="text-[10.5px] text-[#6B7280]">{type.cred} · default zones: {type.defaultZones.join(', ')}</div>
            </div>
            <button onClick={() => setStep('kind')} className="text-[11px] text-[#1F2937] hover:underline">Change</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Person or group name" required><Input value={name} onChange={e => setName(e.target.value)} placeholder={type.id === 'vip' ? 'Hon. Janet Mukwaya' : type.id === 'media' ? 'NTV Crew' : 'Full name or group label'} /></Field>
            <Field label="Organization"><Input value={org} onChange={e => setOrg(e.target.value)} placeholder={type.id === 'media' ? 'NTV Uganda' : type.id === 'vendor' ? 'Akibua Catering' : 'Ministry / Company / Federation'} /></Field>
            <Field label="Contact (phone or email)"><Input value={contact} onChange={e => setContact(e.target.value)} placeholder="+256 700 123 456 or name@org.com" /></Field>
            <Field label="Party size" sub="Use >1 for crews / groups"><Input type="number" min={1} max={50} value={partySize} onChange={e => setPartySize(Math.max(1, parseInt(e.target.value, 10) || 1))} /></Field>
            <Field label="Event" required>
              <EventSelect events={eligibleEvents} value={eventId} onChange={(id) => setEventId(id)} placeholder="Select event…" className="w-full" />
            </Field>
            <Field label="Valid through" required sub="Last day the credential is honoured"><Input type="date" value={validThrough} onChange={e => setValidThrough(e.target.value)} /></Field>
          </div>
          {selectedEvent && (
            <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2 text-[11px] text-[#6B7280]">
              <span className="text-[#1F2937] font-medium">{selectedEvent.name}</span> · {selectedEvent.venue} · {selectedEvent.date}
            </div>
          )}
        </div>
      )}

      {step === 'access' && type && (
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-medium text-[#1F2937]">Zone access</label>
              <button onClick={() => setZones([...type.defaultZones])} className="text-[10.5px] text-[#6B7280] hover:text-[#1F2937]">Reset to {type.label} defaults</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ACCREDITATION_ZONES.map(z => {
                const on = zones.includes(z);
                return (
                  <button key={z} onClick={() => toggleZone(z)} className={`h-7 px-2.5 text-[11.5px] rounded-full border transition-colors ${on ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#1F2937] border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                    {on && <Check className="inline w-3 h-3 mr-1 -mt-0.5" />}{z}
                  </button>
                );
              })}
            </div>
            {zones.length === 0 && <div className="text-[10.5px] text-[#9B1F1F] mt-1.5">Pick at least one zone</div>}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-[#1F2937] mb-1.5">Credential delivery</label>
            <div className="grid grid-cols-2 gap-2">
              {ACCREDITATION_DELIVERY.map(d => (
                <label key={d.id} className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer ${delivery === d.id ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                  <input type="radio" name="delivery" checked={delivery === d.id} onChange={() => setDelivery(d.id)} className="mt-0.5" />
                  <div>
                    <div className="text-[12px] font-medium text-[#1F2937]">{d.label}</div>
                    <div className="text-[10.5px] text-[#6B7280] mt-0.5">{d.sub}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <Field label="Internal notes" sub="Visible to credentialing ops · not printed on the badge"><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Pickup at gate B from 14:00. Photo required at credentialing centre." /></Field>
        </div>
      )}

      <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] -mx-5 -mb-4 mt-4 flex items-center justify-between">
        {step !== 'kind' ? (
          <button onClick={() => setStep(step === 'access' ? 'person' : 'kind')} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED] inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5" />Back</button>
        ) : <div />}
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
          {step === 'person' && <button onClick={() => setStep('access')} disabled={!canContinuePerson} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white">Continue</button>}
          {step === 'access' && <button onClick={issue} disabled={!canIssue} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" />Issue accreditation</button>}
        </div>
      </div>
    </Modal>
  );
};

